import * as Sentry from '@sentry/angular';

import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';

import { AuthService } from './auth.service';

import { environment } from '@env';

@Injectable({
    providedIn: 'root',
})
export class AppService {
    public loading = new BehaviorSubject(false);

    public hideTopBar = new BehaviorSubject(false);

    private locale: BehaviorSubject<string>;

    constructor(private authService: AuthService) {
        this.locale = new BehaviorSubject(this.detectPreferredLocale());
        this.locale.subscribe((newLocale) => {
            console.debug(`[AppService] Locale changed to: ${newLocale}`);
            if (newLocale !== undefined) {
                localStorage.setItem(environment.storageKeys.LOCALE, newLocale);
            }
        });
        this.authService.activeUser.subscribe((user) => {
            if (user !== undefined && user.locale !== this.locale.getValue()) {
                this.setLocale(user.locale);
                this.loading.next(true);
                window.location.reload();
            }
            if (!environment.enableSentry) {
                return;
            }
            if (user !== undefined) {
                Sentry.setUser({ email: user.email, username: `${user.firstName} ${user.lastName}`, id: user.id });
            } else {
                Sentry.setUser(null);
            }
        });
    }

    getLocale(): Observable<string> {
        return this.locale;
    }

    getCurrentLocale(): string {
        return this.locale.getValue() ?? this.detectPreferredLocale();
    }

    setLocale(newLocale: string) {
        // eslint-disable-next-line no-extra-parens
        if (!(environment.supportedLocales as string[]).includes(newLocale)) {
            console.debug(`[AppService] Locale not supported: ${newLocale}!`);
        } else if (newLocale !== this.locale.getValue()) {
            this.locale.next(newLocale);
        }
    }

    clearStoredLocale(): void {
        localStorage.removeItem(environment.storageKeys.LOCALE);
    }

    private detectPreferredLocale(): string {
        const localeSet = localStorage.getItem(environment.storageKeys.LOCALE) as string;
        // eslint-disable-next-line no-extra-parens
        if (localeSet !== undefined && (environment.supportedLocales as string[]).includes(localeSet)) {
            console.debug(`[AppService] Locale detected: ${localeSet}`);
            return localeSet;
        }

        // If not set already, try to detect the best possible match from the browser settings.
        const preferredLocale = window?.navigator?.languages?.find((loc) => loc.substring(0, 2) in environment.supportedLocales);
        console.debug(`[AppService] Browser locale detected: ${preferredLocale}`);
        return preferredLocale?.substring(0, 2) ?? window?.navigator?.language ?? environment.defaultLocale;
    }
}
